import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types/auth.types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  redirectPath?: string;
}

/**
 * Composant pour protéger les routes en fonction de l'authentification et des rôles
 * Si l'utilisateur n'est pas authentifié ou n'a pas le rôle requis, il est redirigé
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  redirectPath = '/login'
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Si les données sont en cours de chargement, afficher un loader
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  // Si l'utilisateur n'est pas authentifié, rediriger vers la page de connexion
  if (!isAuthenticated) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // Si des rôles sont requis, vérifier que l'utilisateur a le rôle nécessaire
  if (requiredRoles.length > 0 && user) {
    // Vérifications spéciales pour admin
    const isAdmin = user.role === 'admin' || user.email === 'adminetudown@admin.com';
    
    // Si l'utilisateur est admin, il a accès à tout
    if (isAdmin) {
      return <>{children}</>;
    }
    
    // Vérifier le rôle de l'utilisateur
    const hasRequiredRole = requiredRoles.includes(user.role as UserRole);
    
    // Si l'utilisateur n'a pas le rôle requis, rediriger vers la page par défaut
    if (!hasRequiredRole) {
      console.log(`🚫 Accès refusé: ${user.email} (${user.role || 'rôle standard'}) -> rôles requis: ${requiredRoles.join(', ')}`);
      return <Navigate to="/" replace />;
    }
  }

  // L'utilisateur est authentifié et a les rôles nécessaires
  return <>{children}</>;
};

export default ProtectedRoute;
